'use client';

import React, { useState, useRef } from 'react';
import { Appearance } from '../page.client';
import { FONT_STYLE_LIST } from '@/app/components/editor-main/static-const';
import { Palette, Baseline, CaseSensitive, Layers, ChevronDown, ChevronUp, Check, Plus, Award, X, Image as ImageIcon, Upload, Trash2 } from 'lucide-react';
import dynamic from 'next/dynamic';

const ImageCropperModal = dynamic(() => import('@/app/components/ImageCropperModal'), { ssr: false });
import { toast } from 'sonner';

// @ts-ignore
import "./../../../../my-fonts.css";

interface NewEnvelopeEditorProps {
    appearance: Appearance;
    onAppearanceChange: (newAppearance: Partial<Appearance>) => void;
    stampUrl: string;
    onStampChange: (url: string) => void;
    customLogo?: string | null;
    onLogoChange?: (newLogoBlobUrl: string | null, blob: Blob | null) => void;
}

// Brand palette (ink, heart, blush, cream, marigold, sage, mauve from tailwind.config.js),
// listed first in the Envelope Color options.
const themeColorPresets = [
    { name: 'Ink', value: '#06233F' },
    { name: 'Night', value: '#11212D' },
    { name: 'Heart', value: '#C93D63' },
    { name: 'Deep Rose', value: '#A8304F' },
    { name: 'Logo Pink', value: '#F883A6' },
    { name: 'Blush', value: '#ECAAAA' },
    { name: 'Dusty Pink', value: '#DBB8BC' },
    { name: 'Cream', value: '#FBEBDC' },
    { name: 'Linen', value: '#F4ECE1' },
    { name: 'Marigold', value: '#F4B740' },
    { name: 'Sage', value: '#5B7F69' },
    { name: 'Soft Sage', value: '#BDD0C3' },
    { name: 'Mauve', value: '#9E8189' },
    { name: 'Soft Mauve', value: '#E8D2C4' },
];

const themeGradientPresets = [
    { name: 'Ink Night', value: 'linear-gradient(135deg, #06233F 0%, #3F5B78 100%)' },
    { name: 'Keepsake Rose', value: 'linear-gradient(135deg, #C93D63 0%, #F883A6 100%)' },
    { name: 'Ink to Rose', value: 'linear-gradient(135deg, #06233F 0%, #C93D63 100%)' },
    { name: 'Blush Cream', value: 'linear-gradient(135deg, #F9C9D5 0%, #FBEBDC 100%)' },
    { name: 'Sunset Heart', value: 'linear-gradient(135deg, #F4B740 0%, #C93D63 100%)' },
    { name: 'Marigold Cream', value: 'linear-gradient(135deg, #FBEBDC 0%, #F4B740 100%)' },
    { name: 'Sage Garden', value: 'linear-gradient(135deg, #BDD0C3 0%, #4E6E5B 100%)' },
    { name: 'Mauve Dusk', value: 'linear-gradient(135deg, #E8D2C4 0%, #9E8189 100%)' },
    { name: 'Paper Linen', value: 'linear-gradient(135deg, #FBF7F1 0%, #E7E1D6 100%)' },
    { name: 'Midnight Gold', value: 'linear-gradient(135deg, #11212D 0%, #D99A1E 100%)' },
];

/** True for light colours, so the selected tick switches to a dark stroke. */
const isLightColor = (hex: string) => {
    const m = hex.replace('#', '').match(/^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
    if (!m) return false;
    const [r, g, b] = m.slice(1).map(v => parseInt(v, 16));
    return (r * 299 + g * 587 + b * 114) / 1000 > 170;
};

const colorPresets = [
    '#0086A0', // Default Teal
    '#FFFFFF', // White
    '#1E293B', // Slate Navy
    '#475569', // Muted Slate
    '#FDA4AF', // Soft Dusty Rose
    '#FCA5A5', // Pastel Blush Red
    '#FDBA74', // Soft Pastel Peach
    '#FEF08A', // Buttercream Yellow
    '#86EFAC', // Soft Mint Green
    '#A7F3D0', // Pastel Sage Green
    '#6EE7B7', // Soft Seafoam
    '#7DD3FC', // Pastel Sky Blue
    '#93C5FD', // Soft Baby Blue
    '#A5B4FC', // Soft Periwinkle
    '#C4B5FD', // Pastel Lavender
    '#F0ABFC', // Soft Lilac
    '#F472B6', // Pastel Rose Pink
    '#D1D5DB', // Soft Neutral Gray
    '#E5E7EB', // Light Soft Platinum
    '#1C325A',  // Navy Blue:  (Seen on the top-left Art Deco envelope)
    '#2D5A3C',  // Dark Forest Green: #2D5A3C (Seen on the mid-left gold foil envelope)
    '#F2E4D9',// Light Iridescent Pink: #F2E4D9 (A base tone for the pearlescent envelope, with reflections )
    '#A1B69D' // Sage Green Linen: #A1B69D (The base color for the textured botanical envelope)
];

// Envelope lists start with the brand palette, followed by the original presets.
const envelopeColorPresets = [...themeColorPresets.map(c => c.value), ...colorPresets];
const allSolidPresets = envelopeColorPresets.map(c => c.toLowerCase());
// Dark brand inks for envelope text — readable on light envelopes; listed first.
const darkTextPresets = [
    { name: 'Ink', value: '#06233F' },
    { name: 'Night', value: '#11212D' },
    { name: 'Charcoal', value: '#26313F' },
    { name: 'Heart', value: '#C93D63' },
    { name: 'Deep Rose', value: '#A8304F' },
    { name: 'Wine', value: '#6B2236' },
    { name: 'Forest Sage', value: '#3F5A4A' },
    { name: 'Plum', value: '#6B5560' },
];
const textColorPresets = [
    ...darkTextPresets.map(c => c.value),
    ...colorPresets.filter(c => !darkTextPresets.some(d => d.value.toLowerCase() === c.toLowerCase())),
];
const allTextPresets = textColorPresets.map(c => c.toLowerCase());
const colorNames: Record<string, string> = Object.fromEntries(
    [...themeColorPresets, ...themeGradientPresets, ...darkTextPresets].map(c => [c.value, c.name])
);

const gradientPresets = [
    ...themeGradientPresets.map(g => g.value),
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
    'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
    'linear-gradient(135deg, #cfd9df 0%, #e2ebf0 100%)',
    'linear-gradient(135deg, #fccb90 0%, #d57eeb 100%)',
    'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'linear-gradient(135deg, #5ee7df 0%, #b490ca 100%)',
    'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)',
    'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
    'linear-gradient(135deg, #fdcbf9 0%, #e6dee9 100%)',
    'linear-gradient(135deg, #13547a 0%, #80d0c7 100%)',
    'linear-gradient(135deg, #ff0844 0%, #ffb199 100%)',
    'linear-gradient(135deg, #b224ef 0%, #7579ff 100%)',
    'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    'linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)'
];

const fontOptions = FONT_STYLE_LIST;

const liningImageOptions = [
    { name: 'Classic', value: '/assets/images/cover/1.png' },
    { name: 'Elegance', value: '/assets/images/cover/2.png' },
    { name: 'Modern', value: '/assets/images/cover/3.png' },
    { name: 'Vintage', value: '/assets/images/cover/4.png' },
    { name: 'Artistic', value: '/assets/images/cover/5.png' },
    { name: 'Royal', value: '/assets/images/cover/6.png' },
    { name: 'Floral', value: '/assets/images/cover/7.png' },
    { name: 'Minimal', value: '/assets/images/cover/8.png' },
    { name: 'Festive', value: '/assets/images/cover/9.png' },
    { name: 'Premium', value: '/assets/images/cover/10.png' },
    { name: 'Golden', value: '/assets/images/cover/11.png' },
    { name: 'Silver', value: '/assets/images/cover/12.png' },
    { name: 'Rose', value: '/assets/images/cover/13.png' },
    { name: 'Sky', value: '/assets/images/cover/14.png' },
    { name: 'Nature', value: '/assets/images/cover/15.png' },
];

const stampOptions = [
    { name: 'Classic', url: '/assets/images/stamps/1.png' },
    { name: 'Elegant', url: '/assets/images/stamps/2.png' },
    { name: 'Modern', url: '/assets/images/stamps/3.png' },
    { name: 'Vintage', url: '/assets/images/stamps/4.png' },
    { name: 'Royal', url: '/assets/images/stamps/5.png' },
    { name: 'Artistic', url: '/assets/images/stamps/6.png' },
    { name: 'Floral', url: '/assets/images/stamps/7.png' },
    { name: 'Minimal', url: '/assets/images/stamps/8.png' },
    { name: 'Festive', url: '/assets/images/stamps/9.png' },
    { name: 'Premium', url: '/assets/images/stamps/10.png' },
    { name: 'Golden', url: '/assets/images/stamps/11.png' },
    { name: 'Silver', url: '/assets/images/stamps/12.png' },
    { name: 'Rose', url: '/assets/images/stamps/13.png' },
    { name: 'Sky', url: '/assets/images/stamps/14.png' },
    { name: 'Nature', url: '/assets/images/stamps/15.png' },
    { name: 'Love', url: '/assets/images/stamps/16.png' },
    { name: 'Star', url: '/assets/images/stamps/17.png' },
    { name: 'Diamond', url: '/assets/images/stamps/18.png' },
    { name: 'Sparkle', url: '/assets/images/stamps/19.png' },
    { name: 'Badge', url: '/assets/images/stamps/20.png' },
];

export default function NewEnvelopeEditor({ appearance, onAppearanceChange, stampUrl, onStampChange, customLogo, onLogoChange }: NewEnvelopeEditorProps) {
    const [openSection, setOpenSection] = useState<'envelope' | 'text' | 'typography' | 'lining' | 'stamp' | 'logo' | null>('envelope');
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const [isSheetVisible, setIsSheetVisible] = useState(false);

    const [isLogoCropperOpen, setIsLogoCropperOpen] = useState(false);
    const [cropperLogoSrc, setCropperLogoSrc] = useState<string>('');
    const logoFileInputRef = useRef<HTMLInputElement>(null);

    const openSheet = () => {
        setIsSheetOpen(true);
        setTimeout(() => setIsSheetVisible(true), 10);
    };

    const closeSheet = () => {
        setIsSheetVisible(false);
        setTimeout(() => setIsSheetOpen(false), 300);
    };

    const toggleSection = (section: 'envelope' | 'text' | 'typography' | 'lining' | 'stamp' | 'logo') => {
        setOpenSection(openSection === section ? null : section);
    };

    const handleLogoFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Please select an image file');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error('File size should not exceed 5MB');
            return;
        }

        const objectUrl = URL.createObjectURL(file);
        setCropperLogoSrc(objectUrl);
        setIsLogoCropperOpen(true);

        if (logoFileInputRef.current) {
            logoFileInputRef.current.value = '';
        }
    };

    const handleLogoCropComplete = (blob: Blob) => {
        setIsLogoCropperOpen(false);
        const blobUrl = URL.createObjectURL(blob);
        onLogoChange?.(blobUrl, blob);
        toast.success('Custom logo cropped and updated');
    };

    const handleRemoveLogo = () => {
        onLogoChange?.(null, null);
        if (logoFileInputRef.current) {
            logoFileInputRef.current.value = '';
        }
        toast.info('Custom logo removed. Default main logo restored.');
    };

    return (
        <div className="space-y-3.5 w-full">
            {/* 1. Envelope Color Accordion */}
            <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden transition-all duration-300">
                <button
                    onClick={() => toggleSection('envelope')}
                    className="w-full flex items-center justify-between p-5 text-left font-semibold text-slate-800 hover:bg-slate-50 transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <Palette className="w-5 h-5 text-indigo-600" />
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-800">Envelope Color</span>
                    </div>
                    {openSection === 'envelope' ? (
                        <ChevronUp className="w-4 h-4 text-slate-500" />
                    ) : (
                        <ChevronDown className="w-4 h-4 text-slate-500" />
                    )}
                </button>

                {openSection === 'envelope' && (
                    <div className="p-5 border-t border-slate-100 bg-white space-y-4">
                        {/* Mode Selector Tabs */}
                        <div className="flex bg-slate-100 p-1 rounded-xl w-full text-xs font-semibold">
                            <button
                                type="button"
                                onClick={() => {
                                    if (appearance.envelopeColor.includes('gradient')) {
                                        onAppearanceChange({ envelopeColor: '#0086A0' });
                                    }
                                }}
                                className={`flex-1 py-1.5 rounded-lg transition-all ${
                                    !appearance.envelopeColor.includes('gradient')
                                        ? 'bg-white text-indigo-600 shadow-sm'
                                        : 'text-slate-500 hover:text-slate-800'
                                }`}
                            >
                                Solid Color
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    if (!appearance.envelopeColor.includes('gradient')) {
                                        onAppearanceChange({ envelopeColor: gradientPresets[0] });
                                    }
                                }}
                                className={`flex-1 py-1.5 rounded-lg transition-all ${
                                    appearance.envelopeColor.includes('gradient')
                                        ? 'bg-white text-indigo-600 shadow-sm'
                                        : 'text-slate-500 hover:text-slate-800'
                                }`}
                            >
                                Gradient
                            </button>
                        </div>

                        {!appearance.envelopeColor.includes('gradient') ? (
                            <div className="flex flex-wrap gap-3.5 items-center">
                                {envelopeColorPresets.map((color) => {
                                    const isSelected = appearance.envelopeColor.toLowerCase() === color.toLowerCase();
                                    return (
                                        <button
                                            type="button"
                                            key={color}
                                            onClick={() => onAppearanceChange({ envelopeColor: color })}
                                            className={`w-9 h-9 rounded-full transition-all duration-200 hover:scale-110 active:scale-95 flex items-center justify-center border border-slate-200 relative ${
                                                isSelected ? 'ring-2 ring-indigo-600 ring-offset-2 shadow-sm' : ''
                                            }`}
                                            style={{ backgroundColor: color }}
                                            title={colorNames[color] || color}
                                        >
                                            {isSelected && (
                                                <Check
                                                    size={14}
                                                    className={isLightColor(color) ? 'text-slate-800' : 'text-white'}
                                                />
                                            )}
                                        </button>
                                    );
                                })}

                                {/* Custom solid color picker */}
                                <div className="relative">
                                    <button
                                        type="button"
                                        className={`w-9 h-9 rounded-full border border-dashed border-slate-300 hover:border-indigo-500 hover:scale-110 active:scale-95 transition-all flex items-center justify-center text-slate-500 bg-slate-50 relative overflow-hidden ${
                                            !allSolidPresets.includes(appearance.envelopeColor.toLowerCase())
                                                ? 'ring-2 ring-indigo-600 ring-offset-2 shadow-sm'
                                                : ''
                                        }`}
                                    >
                                        {!allSolidPresets.includes(appearance.envelopeColor.toLowerCase()) ? (
                                            <div
                                                className="w-full h-full rounded-full flex items-center justify-center"
                                                style={{ backgroundColor: appearance.envelopeColor }}
                                            >
                                                <Check
                                                    size={14}
                                                    className={appearance.envelopeColor === '#ffffff' ? 'text-slate-800' : 'text-white'}
                                                />
                                            </div>
                                        ) : (
                                            <Plus size={16} />
                                        )}
                                        <input
                                            type="color"
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                            value={appearance.envelopeColor.startsWith('#') ? appearance.envelopeColor : '#0086A0'}
                                            onChange={(e) => onAppearanceChange({ envelopeColor: e.target.value })}
                                        />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex flex-wrap gap-3.5 items-center">
                                    {gradientPresets.map((grad, index) => {
                                        const isSelected = appearance.envelopeColor === grad;
                                        return (
                                            <button
                                                type="button"
                                                key={index}
                                                onClick={() => onAppearanceChange({ envelopeColor: grad })}
                                                className={`w-9 h-9 rounded-full transition-all duration-200 hover:scale-110 active:scale-95 flex items-center justify-center border border-slate-200 relative ${
                                                    isSelected ? 'ring-2 ring-indigo-600 ring-offset-2 shadow-sm' : ''
                                                }`}
                                                style={{ background: grad }}
                                                title={colorNames[grad] || `Gradient ${index + 1}`}
                                            >
                                                {isSelected && <Check size={14} className="text-white drop-shadow" />}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Custom Gradient Controls */}
                                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80 space-y-3 text-xs">
                                    <span className="font-semibold text-slate-700 block">Custom Gradient</span>
                                    <div className="flex items-center gap-3">
                                        <label className="flex items-center gap-2 cursor-pointer text-slate-600">
                                            <span>Start:</span>
                                            <input
                                                type="color"
                                                className="w-7 h-7 rounded cursor-pointer border-none bg-transparent"
                                                value={(() => {
                                                    const matches = appearance.envelopeColor.match(/#[a-fA-F0-9]{6}/g);
                                                    return matches?.[0] || '#667eea';
                                                })()}
                                                onChange={(e) => {
                                                    const start = e.target.value;
                                                    const matches = appearance.envelopeColor.match(/#[a-fA-F0-9]{6}/g);
                                                    const stop = matches?.[1] || '#764ba2';
                                                    onAppearanceChange({ envelopeColor: `linear-gradient(135deg, ${start} 0%, ${stop} 100%)` });
                                                }}
                                            />
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer text-slate-600">
                                            <span>End:</span>
                                            <input
                                                type="color"
                                                className="w-7 h-7 rounded cursor-pointer border-none bg-transparent"
                                                value={(() => {
                                                    const matches = appearance.envelopeColor.match(/#[a-fA-F0-9]{6}/g);
                                                    return matches?.[1] || '#764ba2';
                                                })()}
                                                onChange={(e) => {
                                                    const stop = e.target.value;
                                                    const matches = appearance.envelopeColor.match(/#[a-fA-F0-9]{6}/g);
                                                    const start = matches?.[0] || '#667eea';
                                                    onAppearanceChange({ envelopeColor: `linear-gradient(135deg, ${start} 0%, ${stop} 100%)` });
                                                }}
                                            />
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* 2. Text Color Accordion */}
            <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden transition-all duration-300">
                <button
                    onClick={() => toggleSection('text')}
                    className="w-full flex items-center justify-between p-5 text-left font-semibold text-slate-800 hover:bg-slate-50 transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <Baseline className="w-5 h-5 text-indigo-600" />
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-800">Text Color</span>
                    </div>
                    {openSection === 'text' ? (
                        <ChevronUp className="w-4 h-4 text-slate-500" />
                    ) : (
                        <ChevronDown className="w-4 h-4 text-slate-500" />
                    )}
                </button>

                {openSection === 'text' && (
                    <div className="p-5 border-t border-slate-100 bg-white">
                        <div className="flex flex-wrap gap-3.5 items-center">
                            {textColorPresets.map((color) => {
                                const isSelected = appearance.textColor.toLowerCase() === color.toLowerCase();
                                return (
                                    <button
                                        type="button"
                                        key={color}
                                        onClick={() => onAppearanceChange({ textColor: color })}
                                        className={`w-9 h-9 rounded-full transition-all duration-200 hover:scale-110 active:scale-95 flex items-center justify-center border border-slate-200 relative ${
                                            isSelected ? 'ring-2 ring-indigo-600 ring-offset-2 shadow-sm' : ''
                                        }`}
                                        style={{ backgroundColor: color }}
                                        title={colorNames[color] || color}
                                    >
                                        {isSelected && (
                                            <Check
                                                size={14}
                                                className={isLightColor(color) ? 'text-slate-800' : 'text-white'}
                                            />
                                        )}
                                    </button>
                                );
                            })}

                            {/* Custom color picker */}
                            <div className="relative">
                                <button
                                    type="button"
                                    className={`w-9 h-9 rounded-full border border-dashed border-slate-300 hover:border-indigo-500 hover:scale-110 active:scale-95 transition-all flex items-center justify-center text-slate-500 bg-slate-50 relative overflow-hidden ${
                                        !allTextPresets.includes(appearance.textColor.toLowerCase())
                                            ? 'ring-2 ring-indigo-600 ring-offset-2 shadow-sm'
                                            : ''
                                    }`}
                                >
                                    {!allTextPresets.includes(appearance.textColor.toLowerCase()) ? (
                                        <div
                                            className="w-full h-full rounded-full flex items-center justify-center"
                                            style={{ backgroundColor: appearance.textColor }}
                                        >
                                            <Check
                                                size={14}
                                                className={isLightColor(appearance.textColor) ? 'text-slate-800' : 'text-white'}
                                            />
                                        </div>
                                    ) : (
                                        <Plus size={16} />
                                    )}
                                    <input
                                        type="color"
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        value={appearance.textColor}
                                        onChange={(e) => onAppearanceChange({ textColor: e.target.value })}
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* 3. Typography Accordion */}
            <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden transition-all duration-300">
                <button
                    onClick={() => toggleSection('typography')}
                    className="w-full flex items-center justify-between p-5 text-left font-semibold text-slate-800 hover:bg-slate-50 transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <CaseSensitive className="w-5 h-5 text-indigo-600" />
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-800">Typography</span>
                    </div>
                    {openSection === 'typography' ? (
                        <ChevronUp className="w-4 h-4 text-slate-500" />
                    ) : (
                        <ChevronDown className="w-4 h-4 text-slate-500" />
                    )}
                </button>

                {openSection === 'typography' && (
                    <div className="p-5 border-t border-slate-100 bg-white">
                        <div className="relative">
                            <select
                                id="fontFamily"
                                value={appearance.fontFamily}
                                onChange={(e) => onAppearanceChange({ fontFamily: e.target.value })}
                                className={`w-full bg-white border border-slate-200 rounded-xl px-4 py-3.5 text-sm font-medium outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all appearance-none pr-10 cursor-pointer ${appearance.fontFamily}`}
                            >
                                {fontOptions.map((font) => (
                                    <option key={font.style} value={font.style} className={font.style}>
                                        {font.name}
                                    </option>
                                ))}
                            </select>
                            <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                                <ChevronDown className="w-4.5 h-4.5" />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* 4. Inner Lining Accordion */}
            <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden transition-all duration-300">
                <button
                    onClick={() => toggleSection('lining')}
                    className="w-full flex items-center justify-between p-5 text-left font-semibold text-slate-800 hover:bg-slate-50 transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <Layers className="w-5 h-5 text-indigo-600" />
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-800">Inner Lining</span>
                    </div>
                    {openSection === 'lining' ? (
                        <ChevronUp className="w-4 h-4 text-slate-500" />
                    ) : (
                        <ChevronDown className="w-4 h-4 text-slate-500" />
                    )}
                </button>

                {openSection === 'lining' && (
                    <div className="p-5 border-t border-slate-100 bg-white">
                        <div className="grid grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-1 no-scrollbar">
                            {liningImageOptions.map((img) => {
                                const isSelected = appearance.liningImage === img.value;
                                return (
                                    <button
                                        type="button"
                                        key={img.name}
                                        onClick={() => onAppearanceChange({ liningImage: img.value })}
                                        className={`group relative h-16 rounded-xl overflow-hidden transition-all duration-300 border-2 ${
                                            isSelected ? 'border-indigo-600 shadow-md' : 'border-slate-200 hover:border-slate-300'
                                        } hover:scale-[1.01] active:scale-95`}
                                    >
                                        <div className="absolute inset-0 bg-slate-100 pointer-events-none" />
                                        <div
                                            className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105 pointer-events-none"
                                            style={{ backgroundImage: `url(${img.value})` }}
                                        />
                                        <div className="absolute inset-0 bg-black/10 group-hover:bg-black/5 transition-colors pointer-events-none" />
                                        <div className="absolute bottom-0 left-0 right-0 p-1.5 bg-black/55 backdrop-blur-[1px] text-[9px] font-bold text-white uppercase tracking-wider pointer-events-none text-left truncate">
                                            {img.name}
                                        </div>
                                        {isSelected && (
                                            <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-indigo-600 rounded-full flex items-center justify-center shadow-sm pointer-events-none">
                                                <Check size={9} className="text-white font-bold" />
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* 5. Stamps Accordion */}
            <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden transition-all duration-300">
                <button
                    onClick={() => toggleSection('stamp')}
                    className="w-full flex items-center justify-between p-5 text-left font-semibold text-slate-800 hover:bg-slate-50 transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <Award className="w-5 h-5 text-indigo-600" />
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-800">Stamp</span>
                    </div>
                    {openSection === 'stamp' ? (
                        <ChevronUp className="w-4 h-4 text-slate-500" />
                    ) : (
                        <ChevronDown className="w-4 h-4 text-slate-500" />
                    )}
                </button>

                {openSection === 'stamp' && (
                    <div className="p-5 border-t border-slate-100 bg-white space-y-4">
                        <div 
                            onClick={openSheet}
                            className="flex items-center gap-4 p-3 bg-slate-50/60 border border-slate-100 hover:border-indigo-100 hover:bg-indigo-50/10 rounded-xl cursor-pointer transition-all duration-200 group/stamp-preview"
                        >
                            <div className="relative w-14 h-14 bg-white border border-slate-200/80 rounded-lg flex items-center justify-center p-2 shadow-sm transition-transform duration-200 group-hover/stamp-preview:scale-105">
                                <img
                                    src={stampUrl}
                                    alt="Selected Stamp"
                                    className="w-full h-full object-contain"
                                />
                            </div>
                            <div className="flex-1">
                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Current Seal</div>
                                <div className="text-sm font-bold text-slate-800 mt-0.5">
                                    {stampOptions.find(s => s.url === stampUrl)?.name || 'Classic'}
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={openSheet}
                            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-all active:scale-95 shadow-sm hover:shadow flex items-center justify-center gap-2"
                        >
                            <Award className="w-4 h-4" /> Change Stamp
                        </button>
                    </div>
                )}
            </div>

            {/* 6. Custom Logo Accordion */}
            <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden transition-all duration-300">
                <button
                    onClick={() => toggleSection('logo')}
                    className="w-full flex items-center justify-between p-5 text-left font-semibold text-slate-800 hover:bg-slate-50 transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <ImageIcon className="w-5 h-5 text-indigo-600" />
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-800">Custom Logo</span>
                    </div>
                    {openSection === 'logo' ? (
                        <ChevronUp className="w-4 h-4 text-slate-500" />
                    ) : (
                        <ChevronDown className="w-4 h-4 text-slate-500" />
                    )}
                </button>

                {openSection === 'logo' && (
                    <div className="p-5 border-t border-slate-100 bg-white space-y-4">
                        <p className="text-xs text-slate-500 font-medium leading-relaxed">
                            Personalize the top-left of your envelope with your company or personal logo. If removed, the default Loving Ecards logo will be shown.
                        </p>

                        {customLogo ? (
                            <div className="flex flex-col gap-3">
                                <div className="p-3 bg-slate-50 border border-slate-200/80 rounded-xl flex items-center justify-between">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-16 h-12 rounded-lg bg-white border border-slate-200 p-1 flex items-center justify-center overflow-hidden shrink-0 shadow-xs">
                                            <img
                                                src={customLogo}
                                                alt="Custom Logo"
                                                className="max-w-full max-h-full object-contain"
                                            />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-xs font-bold text-slate-800 truncate">Custom Logo Attached</p>
                                            <p className="text-[11px] text-emerald-600 font-medium">Active on envelope</p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleRemoveLogo}
                                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                                        title="Remove custom logo"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => logoFileInputRef.current?.click()}
                                    className="w-full py-2.5 px-3 border border-indigo-200 hover:border-indigo-400 bg-indigo-50/50 hover:bg-indigo-50 text-indigo-700 rounded-xl text-xs font-semibold transition-all flex items-center justify-center gap-2 cursor-pointer"
                                >
                                    <Upload className="w-3.5 h-3.5" />
                                    Change Logo
                                </button>
                            </div>
                        ) : (
                            <div className="p-5 border-2 border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center gap-2.5 bg-slate-50/60 hover:bg-slate-50 transition-colors text-center">
                                <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600">
                                    <Upload className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-slate-700">No custom logo uploaded</p>
                                    <p className="text-[11px] text-slate-400 mt-0.5">Default main logo is currently shown on envelope</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => logoFileInputRef.current?.click()}
                                    className="mt-1 py-2 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-xs hover:shadow active:scale-95 flex items-center gap-1.5 cursor-pointer"
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                    Upload Custom Logo
                                </button>
                            </div>
                        )}

                        <input
                            ref={logoFileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleLogoFileSelect}
                            className="hidden"
                        />
                    </div>
                )}
            </div>

            {/* Glassmorphism Bottom Sheet */}
            {isSheetOpen && (
                <div className="fixed inset-0 z-50 flex items-end justify-center mt-0 top-0 p-0" style={{margin : 0}}>
                    {/* Backdrop */}
                    <div
                        onClick={closeSheet}
                        className={`absolute inset-0 bg-slate-900/30 backdrop-blur-sm transition-opacity duration-300 ease-in-out ${
                            isSheetVisible ? 'opacity-100' : 'opacity-0'
                        }`}
                    />

                    {/* Sheet Container */}
                    <div
                        className={`relative w-full max-w-7xl bg-white/70 backdrop-blur-xl border border-white/40 rounded-t-3xl shadow-2xl overflow-hidden transition-all duration-300 ease-out transform ${
                            isSheetVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
                        } max-h-[80vh] flex flex-col`}
                    >
                        {/* Drag Handle & Header */}
                        <div className="flex-none flex flex-col items-center pt-3 pb-4 px-6 border-b border-white/20">
                            <div className="w-12 h-1.5 bg-slate-400/40 rounded-full mb-3" />
                            <div className="w-full flex items-center justify-between">
                                <div>
                                    <h2 className="text-lg font-bold text-slate-900 leading-tight">
                                        Choose Envelope Stamp
                                    </h2>
                                    <p className="text-xs text-slate-500 font-medium mt-0.5">
                                        Select a beautiful stamp to seal your envelope
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={closeSheet}
                                    className="p-2 hover:bg-white/40 rounded-full transition-colors text-slate-500 hover:text-slate-800"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Stamp Grid */}
                        <div className="flex-1 p-6 overflow-y-auto no-scrollbar max-h-[50vh]">
                            <div className="grid grid-cols-2 lg:grid-cols-6 md:grid-cols-5 sm:grid-cols-4 gap-4">
                                {stampOptions.map((stamp) => {
                                    const isSelected = stampUrl === stamp.url;
                                    return (
                                        <button
                                            type="button"
                                            key={stamp.name}
                                            onClick={() => onStampChange(stamp.url)}
                                            className={`group relative aspect-square rounded-2xl p-4 transition-all duration-300 border flex flex-col items-center justify-center gap-2 cursor-pointer ${
                                                isSelected
                                                    ? 'bg-white/90 border-indigo-200 shadow-lg ring-2 ring-indigo-600/50'
                                                    : 'bg-white/40 border-white/30 hover:bg-white/75 hover:border-white/60 hover:scale-[1.03] hover:shadow-md'
                                            }`}
                                        >
                                            <div className="relative w-20 h-20 transform transition-transform duration-300 group-hover:scale-110">
                                                <img
                                                    src={stamp.url}
                                                    alt={stamp.name}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                            <div className="text-[10px] font-bold text-slate-600 uppercase tracking-tight text-center truncate w-full">
                                                {stamp.name}
                                            </div>
                                            {isSelected && (
                                                <div className="absolute top-2.5 right-2.5 w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center shadow-md">
                                                    <Check size={11} className="text-white font-bold" />
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex-none p-5 border-t border-white/20 bg-white/20 flex justify-end">
                            <button
                                type="button"
                                onClick={closeSheet}
                                className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-all active:scale-95 shadow-sm"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ImageCropperModal
                isOpen={isLogoCropperOpen}
                onCancel={() => setIsLogoCropperOpen(false)}
                imageSrc={cropperLogoSrc}
                cropType="logo"
                aspectRatio={16 / 9}
                onCropComplete={handleLogoCropComplete}
            />
        </div>
    );
}
